import {
  Address,
  AssetKey,
  DisplayMarketAsset,
  getChainKeyFromProtocol,
  GlobalStateService,
  makeAmount,
  PERCENT_ZERO,
  ProtocolKeyService,
  ProtocolStateService,
} from "@silo/types";
import { Array, Effect, Option, pipe, Ref } from "effect";
import { GraphMarket } from "../graph-client/get-graph-markets";
import { ContractsClient } from "../contracts-client";
import { getTokenLabels } from "../utils/get-token-labels";
import {
  calculateRewardsApr,
  convertToUsd,
  getCollateralBaseApr,
} from "@silo/utils";
import {
  getConfigDepositMode,
  getConfigIsDebtAprFixed,
  getConfigPointsByType,
  getProtocolConfig,
  GLOBAL_CONFIG,
} from "@silo/config";
import { MerklCampaignMapService } from "../merkl-client/merkl-campaign-map";
import { getMerklRewardsByType } from "../utils/get-merkl-reward";
import { getDebtTotalApr, getDepositTotalApr } from "./utils";
import { IncentivizedShareTokensService } from "./types";
import { trackIncentivizedShareTokens } from "./track-incentivized-share-tokens";

export type GetPrimaryDisplayMarketAsset = ReturnType<
  typeof getPrimaryDisplayMarketAsset
>;
export type GetPrimaryDisplayMarketAssetSuccess =
  Effect.Effect.Success<GetPrimaryDisplayMarketAsset>;
export type GetPrimaryDisplayMarketAssetError =
  Effect.Effect.Error<GetPrimaryDisplayMarketAsset>;
export type GetPrimaryDisplayMarketAssetContext =
  Effect.Effect.Context<GetPrimaryDisplayMarketAsset>;

interface GetPrimaryDisplayMarketAssetParams {
  marketAddress: Address;
  assetKey: AssetKey;
  tokenAddress: Address;
  marketAssets: GraphMarket["marketAssets"];
}

export function getPrimaryDisplayMarketAsset({
  marketAddress,
  assetKey,
  tokenAddress,
  marketAssets,
}: GetPrimaryDisplayMarketAssetParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const chainKey = getChainKeyFromProtocol(protocolKey);

    const {
      inputToken,
      liquidity,
      priceUsd,
      rates,
      depositedTotal,
      protectedTotal,
      debtTotal,
      sToken: { collateralShareToken },
      spToken: { protectedShareToken },
      dToken: { debtShareToken },
    } = yield* pipe(
      marketAssets,
      Array.findFirst((asset) => asset.inputToken.tokenAddress === tokenAddress)
    );

    const {
      collateralEmissionPerSecond,
      protectedEmissionPerSecond,
      debtEmissionPerSecond,
    } = yield* ContractsClient.getRewardsEmissions({
      collateralShareToken,
      protectedShareToken,
      debtShareToken,
    });

    yield* trackIncentivizedShareTokens({
      collateralShareToken,
      collateralEmissionPerSecond,
      protectedShareToken,
      protectedEmissionPerSecond,
      debtShareToken,
      debtEmissionPerSecond,
    });

    const { decimals } = inputToken;
    const { symbol, name } = yield* getTokenLabels({ ...inputToken, chainKey });

    const liquidityUsd = convertToUsd(liquidity, decimals, priceUsd);
    const tvlUsd = convertToUsd(depositedTotal, decimals, priceUsd);

    const { protocolShareFee, rewardTokenPriceUsd } =
      yield* ProtocolStateService;

    const rawCollateralBaseApr = pipe(
      rates,
      Array.findFirst((rate) => rate.side === "LENDER"),
      Option.map((rate) => rate.rate),
      Option.getOrElse(() => PERCENT_ZERO)
    );

    const collateralBaseApr = getCollateralBaseApr(
      protocolShareFee,
      rawCollateralBaseApr
    );

    const { nativeRewards } = getProtocolConfig(protocolKey);
    const collateralTotal = pipe(depositedTotal - protectedTotal, makeAmount);

    const collateralRewardsApr = nativeRewards
      ? calculateRewardsApr({
          emissionPerSecond: collateralEmissionPerSecond,
          rewardTokenPriceUsd,
          rewardTokenDecimals: nativeRewards.decimals,
          totalTokens: collateralTotal,
          tokenPriceUsd: priceUsd,
          tokenDecimals: decimals,
        })
      : PERCENT_ZERO;

    const protectedRewardsApr = nativeRewards
      ? calculateRewardsApr({
          emissionPerSecond: protectedEmissionPerSecond,
          rewardTokenPriceUsd,
          rewardTokenDecimals: nativeRewards.decimals,
          totalTokens: protectedTotal,
          tokenPriceUsd: priceUsd,
          tokenDecimals: decimals,
        })
      : PERCENT_ZERO;

    const debtBaseApr = pipe(
      rates,
      Array.findFirst((rate) => rate.side === "BORROWER"),
      Option.map((rate) => rate.rate),
      Option.getOrElse(() => PERCENT_ZERO)
    );

    const debtRewardsApr = nativeRewards
      ? calculateRewardsApr({
          emissionPerSecond: debtEmissionPerSecond,
          rewardTokenPriceUsd,
          rewardTokenDecimals: nativeRewards.decimals,
          totalTokens: debtTotal,
          tokenPriceUsd: priceUsd,
          tokenDecimals: decimals,
        })
      : PERCENT_ZERO;

    const merklCampaignMap = yield* MerklCampaignMapService;

    const assetMerklCampaigns = merklCampaignMap.getOrEmpty({
      protocolKey,
      marketAddress,
      tokenAddress,
    });

    const { collateralMerklRewards, protectedMerklRewards, debtMerklRewards } =
      getMerklRewardsByType(assetMerklCampaigns, {
        tokenAddress,
        symbol,
        decimals,
        collateralTotal,
        protectedTotal,
        debtTotal,
        priceUsd,
      });

    const { collateralPoints, protectedPoints, debtPoints } =
      getConfigPointsByType({
        protocolKey,
        marketAddress,
        assetKey,
      });

    const isDebtAprFixed = getConfigIsDebtAprFixed({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const depositMode = getConfigDepositMode({
      protocolKey,
      marketAddress,
      assetKey,
    });

    const globalState = yield* GlobalStateService;
    const { revenue } = GLOBAL_CONFIG;

    const revenueApr =
      revenue.protocolKey === protocolKey &&
      revenue.marketAddress === marketAddress &&
      revenue.targetTokenAddress === tokenAddress
        ? globalState.revenueApr
        : PERCENT_ZERO;

    const depositTotalApr = getDepositTotalApr({
      collateralBaseApr,
      collateralRewardsApr,
      collateralMerklRewards,
      protectedRewardsApr,
      protectedMerklRewards,
      depositMode,
      revenueApr,
    });

    const debtTotalApr = getDebtTotalApr({
      debtBaseApr,
      debtRewardsApr,
      debtMerklRewards,
    });

    const displayMarketAsset: DisplayMarketAsset = {
      protocolKey,
      chainKey,
      tokenAddress,
      symbol,
      name,
      decimals,
      liquidityUsd,
      tvlUsd,
      collateralBaseApr,
      collateralRewardsApr,
      collateralMerklRewards,
      collateralPoints,
      protectedRewardsApr,
      protectedMerklRewards,
      protectedPoints,
      debtBaseApr,
      debtRewardsApr,
      debtMerklRewards,
      debtPoints,
      isDebtAprFixed,
      depositMode,
      depositTotalApr,
      debtTotalApr,
    };

    return displayMarketAsset;
  });
}
